import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import { FixedSizeGrid as Grid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { primeImageCache } from '../utils/imageCache';

import SearchFilters from '../components/SearchFilters';
import SearchTabs from '../components/SearchTabs';
import LayoutToggle from '../components/LayoutToggle';
import ItemCard from '../components/ItemCard';
import ItemDetailPanel from '../components/ItemDetailPanel';
import BagDetailPanel from '../components/BagDetailPanel';
import PluDetailPanel from '../components/PluDetailPanel';
import UPCDetailPanel from '../components/UPCDetailPanel';
import DisplayViewerPanel from '../components/DisplayViewerPanel';

import {
  PageWrapper,
  FiltersContainer,
  ContentWrapper,
  TopBar,
  Spinner,
  SkeletonCard,
  NoResultsMessage,
  ScrollableContent,
  VirtualizedGridWrapper,
} from '../styles/PackageIndexStyles';

const CARD_WIDTH = 260;
const CARD_HEIGHT = 320;
const GUTTER = 16;

const IMAGE_BASE_BAGS = 'https://thevaultmedia.azureedge.net/vault-images/PIDBags/';

const buildCdnUrl = (folder, filename) => {
  const base = 'https://thevaultmedia.azureedge.net/vault-images';
  const encoded = encodeURIComponent(filename).replace(/%2F/g, '/');
  return `${base}/${folder}/${encoded}`;
};

// Preload and cache CDN image URLs to keep them sticky while scrolling and tabbing
const preloadImages = (items, folder) => {
  const urls = new Map();
  items.forEach(group => {
    const imgs = Array.isArray(group.Images) ? group.Images : (group.Picture ? [group.Picture] : []);
    imgs.forEach(name => {
      if (!name) return;
      const isAbsolute = /^https?:\/\//i.test(name);
      const url = isAbsolute ? name : buildCdnUrl(folder, name);
      const key = `${folder}/${name}`;
      if (!urls.has(key)) urls.set(key, url);
    });
  });

  urls.forEach((url, key) => {
    primeImageCache(key, url);
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  });
};

const RenderCardCell = React.memo(({ columnIndex, rowIndex, style, data }) => {
  const { groupedData, activeTab, layout, onCardClick, columnCount } = data;
  const index = rowIndex * columnCount + columnIndex;
  if (index >= groupedData.length) return null;

  const group = groupedData[index];
  return (
    <div
      style={{ ...style, padding: `${GUTTER / 2}px` }}
      key={group.MethodName || group.LabelID || group.UniqueID}
    >
      <ItemCard
        group={group}
        activeTab={activeTab}
        layout={layout}
        onClick={() => onCardClick(group)}
      />
    </div>
  );
});

const PackageIndexPage = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'Methods';
  });
  const [layout, setLayout] = useState('grid');

  const [unfilteredItems, setUnfilteredItems] = useState([]);
  const [, setAllItems] = useState([]); // legacy; no longer used for rendering

  // Normalized stores by ID
  const [methodsById, setMethodsById] = useState({});   // key: MethodName (acts as ID here)
  const [bagsById, setBagsById] = useState({});         // key: LabelID
  const [pluById, setPluById] = useState({});           // key: LabelID
  const [upcById, setUpcById] = useState({});           // key: UniqueID or composite
  const [displaysById, setDisplaysById] = useState({}); // key: UniqueID

  // IDs that pass current filter
  const [filteredIds, setFilteredIds] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bagFilters, setBagFilters] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const prevTabRef = useRef('Methods');
  const itemDataRef = useRef({});

  const groupByMethod = (items) => {
    const map = new Map();
    items.forEach(item => {
      if (!map.has(item.MethodName)) {
        const images = Array.isArray(item.Images) ? item.Images : [];
        map.set(item.MethodName, {
          MethodName: item.MethodName,
          Picture: images.length > 0 ? images[0] : '',
          Images: images,
          Items: [],
        });
      }
      map.get(item.MethodName).Items.push(item);
    });
    return Array.from(map.values());
  };

  // Active map by tab
  const activeMap = useMemo(() => (
    activeTab === 'Methods'
      ? methodsById
      : activeTab === 'Bags'
      ? bagsById
      : activeTab === 'PLU'
      ? pluById
      : activeTab === 'Displays'
      ? displaysById
      : upcById
  ), [activeTab, methodsById, bagsById, pluById, displaysById, upcById]);

  // Entities to render derived from filtered IDs and the active map
  const groupedData = useMemo(() => {
    const entities = filteredIds.map(id => activeMap[id]).filter(Boolean);

    if (activeTab === 'Methods') {
      return entities.sort((a, b) =>
        (a.MethodName || '').localeCompare((b.MethodName || ''), undefined, { sensitivity: 'base' })
      );
    }

    if (activeTab === 'Bags') {
      return entities.sort((a, b) => {
        const retailerA = a.Items[0]?.Retailer || '';
        const retailerB = b.Items[0]?.Retailer || '';
        const labelA = a.LabelID || '';
        const labelB = b.LabelID || '';
        const isKRP_A = retailerA.toUpperCase() === 'KRP';
        const isKRP_B = retailerB.toUpperCase() === 'KRP';
        if (isKRP_A && !isKRP_B) return -1;
        if (!isKRP_A && isKRP_B) return 1;
        const retailerCompare = retailerA.localeCompare(retailerB, undefined, { sensitivity: 'base' });
        if (retailerCompare !== 0) return retailerCompare;
        return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
      });
    }

    if (activeTab === 'PLU') {
      return entities.sort((a, b) => {
      const retailerA = a.Items[0]?.Retailer || '';
      const retailerB = b.Items[0]?.Retailer || '';
      const labelA = a.LabelID || '';
      const labelB = b.LabelID || '';
      const isKRP_A = retailerA.toUpperCase() === 'KRP';
      const isKRP_B = retailerB.toUpperCase() === 'KRP';
      if (isKRP_A && !isKRP_B) return -1;
      if (!isKRP_A && isKRP_B) return 1;
      const retailerCompare = retailerA.localeCompare(retailerB, undefined, { sensitivity: 'base' });
      if (retailerCompare !== 0) return retailerCompare;
      return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
      });
    }

    if (activeTab === 'UPC') {
      return entities.sort((a, b) => {
      const commodityCompare = String(a.Commodity || '').localeCompare(String(b.Commodity || ''), undefined, { sensitivity: 'base' });
      if (commodityCompare !== 0) return commodityCompare;
      const retailerCompare = String(a.Retailer || '').localeCompare(String(b.Retailer || ''), undefined, { sensitivity: 'base' });
      if (retailerCompare !== 0) return retailerCompare;
      const wa = Number.parseFloat(String(a.Weight ?? '')) || 0;
      const wb = Number.parseFloat(String(b.Weight ?? '')) || 0;
      return wa - wb;
      });
    }

    if (activeTab === 'Displays') {
      return entities.sort((a, b) =>
        String(a.ProductID || a.Description || '').localeCompare(
          String(b.ProductID || b.Description || ''),
          undefined,
          { sensitivity: 'base' }
        )
      );
    }
  }, [filteredIds, activeMap, activeTab]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'Methods') {
        const [res, commoditiesRes, sizesRes] = await Promise.all([
          authAxios.get('/api/pidmethods?statuses=Active,Incoming'),
          authAxios.get('/api/commodities'),
          authAxios.get('/api/sizeoptions'),
        ]);

        const commodityMap = Object.fromEntries(
          commoditiesRes.data.map(c => [c.UniqueID, c.Commodity])
        );
        const sizeMap = Object.fromEntries(
          sizesRes.data.map(s => [s.UniqueID, s.SizeOption])
        );

        const enriched = res.data.map(method => {
          const resolvedSizes = (method.SizeOptionIDs || '')
            .split(',')
            .filter(Boolean)
            .map(id => sizeMap[id] || id);

          return {
            ...method,
            Commodity: commodityMap[Number(method.CommodityUniqueID)] || 'Unknown Commodity',
            ResolvedSizes: resolvedSizes,
          };
        });

        setUnfilteredItems(enriched);
        setAllItems(enriched);

        // Build normalized Methods map (grouped by MethodName for card identity)
        const methodGroups = groupByMethod(enriched);
        const map = Object.fromEntries(methodGroups.map(g => [g.MethodName, g]));
        setMethodsById(map);
        // Don't set filteredIds here - let SearchFilters handle initial filtering via onFilterChange
        setTimeout(() => preloadImages(enriched, 'PIDMethods'), 0);

      } else if (activeTab === 'Bags') {
        const [res, stylesRes, sizesRes] = await Promise.all([
          authAxios.get('/api/pidbags/index?statuses=Active,Incoming'),
          authAxios.get('/api/packtypes'),
          authAxios.get('/api/sizeoptions'),
        ]);

        const styleMap = Object.fromEntries(
          stylesRes.data.map(s => [s.UniqueID, s.Style])
        );
        const sizeMap = Object.fromEntries(
          sizesRes.data.map(s => [s.UniqueID, s.SizeOption])
        );

        const allBagItems = [];
        res.data.forEach(group => {
          group.Items.forEach(item => {
            allBagItems.push({
              ...item,
              LabelID: group.LabelID,
              Images: Array.isArray(group.Images) ? group.Images : [],
              Picture:
                Array.isArray(group.Images) && group.Images.length > 0
                  ? group.Images[0]
                  : ''
            });
          });
        });

        setBagFilters({
          Retailers: [...new Set(allBagItems.map(b => b.Retailer).filter(Boolean))],
          Commodities: [...new Set(allBagItems.map(b => b.CommodityName).filter(Boolean))],
          Types: [...new Set(allBagItems.map(b => b.Type).filter(Boolean))],
        });

        const normalizedGroups = res.data.map(group => {
          const imgs = Array.isArray(group.Images) ? group.Images : [];
          const fixedImages = imgs.map(img =>
            img.startsWith('http') ? img : `${IMAGE_BASE_BAGS}${img}`
          );
          const picture = fixedImages.length > 0 ? fixedImages[0] : '';

          const enrichedItems = group.Items.map(item => {
            const resolvedSizes = (item.SizeOptionIDs || '')
              .split(',')
              .filter(Boolean)
              .map(id => sizeMap[id] || id);

            // Resolve styles: support either StyleUniqueID or comma-separated Styles
            const resolvedStyles = String(item.Styles || '')
              .split(',')
              .map(s => Number(String(s).trim()))
              .filter(n => Number.isFinite(n))
              .map(id => styleMap[id])
              .filter(Boolean);

            const styleName = item.StyleUniqueID !== undefined
              ? (styleMap[Number(item.StyleUniqueID)] || null)
              : (resolvedStyles[0] || null);

            return {
              ...item,
              Commodity: item.CommodityName || 'Unknown Commodity',
              Style: styleName || (item.Style || 'Unknown Style'),
              ResolvedStyles: resolvedStyles,
              ResolvedSizes: resolvedSizes,
            };
          });

          return {
            ...group,
            Images: fixedImages,
            Picture: picture,
            Items: enrichedItems,
          };
        });

        setUnfilteredItems(normalizedGroups);
        setAllItems(normalizedGroups);

        // Build Bags map by LabelID, merging duplicates just in case API returned multiple entries
        const bagMap = {};
        for (const g of normalizedGroups) {
          const key = g.LabelID;
          if (!bagMap[key]) {
            bagMap[key] = { ...g, Images: [...(g.Images || [])], Items: [...(g.Items || [])] };
          } else {
            const existing = bagMap[key];
            const imgSet = new Set([...(existing.Images || []), ...(g.Images || [])]);
            existing.Images = Array.from(imgSet);
            existing.Items = [...(existing.Items || []), ...(g.Items || [])];
          }
        }
        setBagsById(bagMap);
        // Don't set filteredIds here - let SearchFilters handle initial filtering via onFilterChange
        setTimeout(() => preloadImages(normalizedGroups, 'PIDBags'), 0);

      } else if (activeTab === 'PLU') {
        const [res, commoditiesRes, sizesRes] = await Promise.all([
          authAxios.get('/api/pidplu?statuses=Active,Incoming'),
          authAxios.get('/api/commodities'),
          authAxios.get('/api/sizeoptions'),
        ]);

        const commodityMap = Object.fromEntries(
          commoditiesRes.data.map(c => [c.UniqueID, c.Commodity])
        );
        const sizeMap = Object.fromEntries(
          sizesRes.data.map(s => [s.UniqueID, s.SizeOption])
        );

        const enriched = await Promise.all(res.data.map(async (plu) => {
          let imagePath = 'https://thevaultmedia.blob.core.windows.net/vault-images/PIDPLU/PLUFallback.webp';
          let imageArray = [];
          try {
            const imageRes = await authAxios.get(`/api/pidplu/images?pluId=${plu.UniqueID}`);
            if (Array.isArray(imageRes.data) && imageRes.data.length > 0) {
              imageArray = imageRes.data;
              imagePath = imageRes.data[0];
            }
          } catch {
            console.warn(`Could not load image for PLU ${plu.LabelID}`);
          }

          const resolvedSizes = (plu.SizeOptionIDs || '')
            .split(',')
            .filter(Boolean)
            .map(id => sizeMap[id] || id);

          const resolvedImportSizes = (plu.ImportSizeIDs || '')
            .split(',')
            .filter(Boolean)
            .map(id => sizeMap[id] || id);

          return {
            ...plu,
            Commodity: commodityMap[Number(plu.CommodityUniqueID)] || 'Unknown Commodity',
            Picture: imagePath,
            Images: imageArray,
            ResolvedSizes: resolvedSizes,
            ResolvedImportSizes: resolvedImportSizes,
          };
        }));

        setUnfilteredItems(enriched);
        setAllItems(enriched);

        // Build PLU map by LabelID (aggregate items per label if multiple)
        const pluMap = {};
        enriched.forEach(item => {
          const id = item.LabelID;
          if (!id) return;
          if (!pluMap[id]) {
            pluMap[id] = {
              LabelID: id,
              Picture: item.Picture,
              Images: Array.isArray(item.Images) ? item.Images : [],
              Items: [],
            };
          }
          // Append item
          pluMap[id].Items.push(item);
          // Prefer first non-empty images/picture already set
        });
        setPluById(pluMap);
        // Don't set filteredIds here - let SearchFilters handle initial filtering via onFilterChange
        setTimeout(() => preloadImages(enriched, 'PIDPLU'), 0);
      } else if (activeTab === 'UPC') {
        const [res] = await Promise.all([
          authAxios.get('/api/pidupc?statuses=Active,Incoming')
        ]);

        const items = (res.data || []).map(row => ({
          ...row,
          // for cards: Commodity already present; will render barcode in detail panel
        }));

        setUnfilteredItems(items);
        setAllItems(items);

        // Key by UniqueID
        const upcMap = {};
        items.forEach(it => {
          const id = it.UniqueID;
          if (!id) return;
          upcMap[id] = it;
        });
        setUpcById(upcMap);
        // Don't set filteredIds here - let SearchFilters handle initial filtering via onFilterChange
      } else if (activeTab === 'Displays') {
        const [res] = await Promise.all([
          authAxios.get('/api/piddisplays/index?statuses=Active,Incoming'),
        ]);

        const displays = (res.data || []).map(d => {
          // Use first image (if any) as Picture for the card
          const images = Array.isArray(d.Images) ? d.Images : [];
          const picture = images.length > 0 ? images[0] : '';
          return {
            ...d,
            Picture: picture,
          };
        });

        setUnfilteredItems(displays);
        setAllItems(displays);

        const displayMap = {};
        displays.forEach(d => {
          if (!d.UniqueID) return;
          displayMap[d.UniqueID] = d;
        });
        setDisplaysById(displayMap);
        setTimeout(() => preloadImages(displays, 'PIDDisplays'), 0);
      }
    } catch (err) {
      console.error('[PackageIndexPage] Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      setResetKey(prev => prev + 1);
      prevTabRef.current = activeTab;
    }
    document.body.style.overflow = 'hidden';
    fetchData();
    return () => {
      document.body.style.overflow = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Only depend on activeTab, fetchData is stable via useCallback

  const handleFilter = useCallback((filteredRows) => {
    // Compute inclusion IDs only; do not trim entities
    if (activeTab === 'Methods') {
      const ids = Array.from(new Set((filteredRows || []).map(x => x.MethodName).filter(Boolean)));
      setFilteredIds(ids);
      return;
    }
    if (activeTab === 'Bags') {
      const ids = Array.from(new Set((filteredRows || []).map(g => g.LabelID).filter(Boolean)));
      setFilteredIds(ids);
      return;
    }
    if (activeTab === 'PLU') {
      const ids = Array.from(new Set((filteredRows || []).map(x => x.LabelID).filter(Boolean)));
      setFilteredIds(ids);
      return;
    }
    if (activeTab === 'UPC') {
      const ids = Array.from(new Set((filteredRows || []).map(x => x.UniqueID).filter(Boolean)));
      setFilteredIds(ids);
      return;
    }
    if (activeTab === 'Displays') {
      const ids = Array.from(new Set((filteredRows || []).map(x => x.UniqueID).filter(Boolean)));
      setFilteredIds(ids);
      return;
    }
  }, [activeTab]);

  const handleCardClick = useCallback((group) => {
    setSelectedGroup(group);
  }, []);

  const showSkeletons = loading && groupedData.length === 0;

  return (
    <PageWrapper>
      <FiltersContainer>
        <SearchFilters
          key={resetKey}
          activeTab={activeTab}
          allItems={unfilteredItems}
          onFilterChange={handleFilter}
          bagFilters={activeTab === 'Bags' ? bagFilters : null}
        />
      </FiltersContainer>

      <ContentWrapper>
        <TopBar>
          <SearchTabs
            activeTab={activeTab}
            setActiveTab={(tab) => {
              setAllItems([]);
              setUnfilteredItems([]);
              setMethodsById({});
              setBagsById({});
              setPluById({});
              setUpcById({});
              setDisplaysById({});
              setFilteredIds([]);
              setActiveTab(tab);
            }}
          />
          <LayoutToggle layout={layout} setLayout={setLayout} />
        </TopBar>

        <ScrollableContent>
          {loading && groupedData.length > 0 && <Spinner />}

          {showSkeletons ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : groupedData.length === 0 ? (
            <NoResultsMessage>No results found</NoResultsMessage>
          ) : (
            <VirtualizedGridWrapper>
              <AutoSizer>
                {({ height, width }) => {
                  const columnCount = Math.floor((width + GUTTER) / (CARD_WIDTH + GUTTER));
                  const rowCount = Math.ceil(groupedData.length / columnCount);

                  itemDataRef.current = {
                    groupedData,
                    activeTab,
                    layout,
                    onCardClick: handleCardClick,
                    columnCount,
                  };

                  return (
                    <Grid
                      key={resetKey}
                      columnCount={columnCount}
                      columnWidth={CARD_WIDTH + GUTTER}
                      height={height}
                      rowCount={rowCount}
                      rowHeight={CARD_HEIGHT + GUTTER}
                      width={width}
                      overscanRowCount={6}
                      itemData={itemDataRef.current}
                    >
                      {RenderCardCell}
                    </Grid>
                  );
                }}
              </AutoSizer>
            </VirtualizedGridWrapper>
          )}
        </ScrollableContent>
      </ContentWrapper>

      {selectedGroup && activeTab === 'Methods' && (
        <ItemDetailPanel group={selectedGroup} onClose={() => setSelectedGroup(null)} />
      )}
      {selectedGroup && activeTab === 'Bags' && (
        <BagDetailPanel group={selectedGroup} onClose={() => setSelectedGroup(null)} />
      )}
      {selectedGroup && activeTab === 'PLU' && (
        <PluDetailPanel group={selectedGroup} onClose={() => setSelectedGroup(null)} />
      )}
      {selectedGroup && activeTab === 'UPC' && (
        <UPCDetailPanel group={selectedGroup} onClose={() => setSelectedGroup(null)} />
      )}
      {selectedGroup && activeTab === 'Displays' && (
        <DisplayViewerPanel
          group={selectedGroup}
          onClose={() => setSelectedGroup(null)}
        />
      )}
    </PageWrapper>
  );
};

export default PackageIndexPage;
